import { DataScienceModule } from '../../types';

export const MLOPS_MODULE: DataScienceModule = {
  id: 'ds-mlops',
  title: 'MLOps & Production Deployment',
  description: 'Learn to deploy, monitor, and maintain machine learning models in production environments.',
  duration: '3 hours',
  difficulty: 'advanced',
  objectives: [
    'Implement CI/CD pipelines for ML models',
    'Set up model monitoring and alerting',
    'Handle model versioning and rollbacks',
    'Scale ML systems for production'
  ],
  content: `# MLOps & Production Deployment

MLOps applies DevOps principles to machine learning, ensuring reliable and scalable ML solutions.

## Core Principles

**Automation**: Automated training, validation, CI/CD pipelines, and infrastructure as code.

**Monitoring**: Track model performance, data drift, infrastructure health, and business metrics.

**Reproducibility**: Version control for code, data, and models with experiment tracking.

## CI/CD for Machine Learning

- Automated testing of data pipelines
- Model validation and quality checks
- Canary releases and A/B testing
- Blue-green deployments with rollback strategies

## Model Monitoring

**Performance Tracking**: Accuracy metrics, latency, resource usage, and error rates.

**Drift Detection**: Statistical tests (KS test, PSI) for feature distribution changes.

**Business Impact**: Revenue metrics, user experience scores, and cost optimization.

## Model Versioning

- Centralized model registry with version tracking
- Experiment logging with hyperparameters and metrics
- Artifact storage with metadata management
- A/B testing frameworks for gradual rollouts`,
  codeExamples: [
    {
      title: 'Model Monitoring Class',
      language: 'python',
      code: `import pandas as pd
from datetime import datetime

class ModelMonitor:
    def __init__(self, threshold=0.05):
        self.threshold = threshold
        self.baseline_stats = None
        
    def set_baseline(self, X_train):
        self.baseline_stats = {
            'mean': X_train.mean().to_dict(),
            'std': X_train.std().to_dict()
        }
        
    def detect_drift(self, X_new):
        drift_features = []
        for feature in X_new.columns:
            baseline = self.baseline_stats['mean'][feature]
            current = X_new[feature].mean()
            change = abs(current - baseline) / abs(baseline)
            if change > self.threshold:
                drift_features.append(feature)
        return len(drift_features) > 0, drift_features

# Usage
monitor = ModelMonitor()
monitor.set_baseline(X_train)
drift, features = monitor.detect_drift(X_test)
print(f"Drift detected: {drift}, Features: {features}")`,
      explanation: 'A simple model monitoring class that detects data drift by comparing feature statistics.'
    }
  ],
  quiz: {
    questions: [
      {
        id: 'mlops-q1',
        question: 'What is the primary purpose of MLOps?',
        options: [
          'To replace data scientists with engineers',
          'To apply DevOps principles to ML systems',
          'To eliminate the need for model testing',
          'To reduce model accuracy'
        ],
        correctAnswer: 1,
        explanation: 'MLOps applies DevOps principles to ensure reliable, scalable ML systems.'
      },
      {
        id: 'mlops-q2',
        question: 'What does data drift detection measure?',
        options: [
          'Model training speed',
          'Changes in feature distributions over time',
          'Network latency',
          'Database query performance'
        ],
        correctAnswer: 1,
        explanation: 'Data drift detection monitors changes in input feature distributions.'
      },
      {
        id: 'mlops-q3',
        question: 'Which deployment strategy allows gradual rollout?',
        options: [
          'Big bang deployment',
          'Canary release',
          'Manual deployment',
          'No deployment'
        ],
        correctAnswer: 1,
        explanation: 'Canary releases deploy to a small subset of users before full rollout.'
      },
      {
        id: 'mlops-q4',
        question: 'What is a model registry used for?',
        options: [
          'Storing user passwords',
          'Centralized model storage and version tracking',
          'Email notifications',
          'Database backups'
        ],
        correctAnswer: 1,
        explanation: 'Model registries provide centralized storage with version tracking and metadata.'
      },
      {
        id: 'mlops-q5',
        question: 'Why is experiment tracking important in MLOps?',
        options: [
          'To increase model size',
          'To reproduce results and compare experiments',
          'To slow down training',
          'To remove model versions'
        ],
        correctAnswer: 1,
        explanation: 'Experiment tracking enables reproducibility and comparison of different runs.'
      }
    ],
    passThreshold: 70
  },
  practice: {
    task: 'Implement a simple health check function for a deployed model.',
    starter: `def health_check(model, X_sample):
    """
    Perform a health check on the model.
    Return a dict with status and any issues found.
    """
    # Your code here
    pass`,
    solution: `def health_check(model, X_sample):
    try:
        predictions = model.predict(X_sample)
        return {
            'status': 'healthy',
            'predictions_made': len(predictions),
            'timestamp': datetime.now().isoformat()
        }
    except Exception as e:
        return {
            'status': 'unhealthy',
            'error': str(e)
        }`,
    hints: [
      'Use try-except to catch prediction errors',
      'Return a dictionary with status information',
      'Include a timestamp for logging'
    ]
  }
};
